import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get, patch } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import Modal from '../../../../components/modal/Modal'
import { KycStatusPill, waitingDays } from './KycReview'
import RejectKycModal from './RejectKycModal'
import { IntelligentCheckList, IntelligentCheckSummary, IntelligentCheckBadge } from './IntelligentCheck'
import './KycDetail.scss'

type DocKey = 'idDocument' | 'selfieWithId' | 'proofOfAddress'

const DOC_TABS: { key: DocKey; label: string; icon: string }[] = [
  { key: 'idDocument', label: 'Photo ID', icon: 'mdi:card-account-details-outline' },
  { key: 'selfieWithId', label: 'Selfie with ID', icon: 'mdi:account-box-outline' },
  { key: 'proofOfAddress', label: 'Proof of Address', icon: 'mdi:home-city-outline' },
]

const DOC_TYPE_LABELS: { [key: string]: string } = {
  nin_slip: 'NIN Slip',
  drivers_licence: "Driver's Licence",
  passport: 'International Passport',
  utility_bill: 'Utility Bill',
  bank_statement: 'Bank Statement',
  tenancy_agreement: 'Tenancy Agreement',
}

const KycDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, notify } = useGlobalContext()

  const [submission, setSubmission] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [activeDoc, setActiveDoc] = useState<DocKey>('idDocument')
  const [showNin, setShowNin] = useState(false)
  const [lightbox, setLightbox] = useState(false)
  const [showApprove, setShowApprove] = useState(false)
  const [showReject, setShowReject] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const canReview =
    profile?.role === 'super admin' || profile?.role === 'customer service'

  const fetchDetail = () => {
    setLoading(true)
    get(`kyc/admin/${id}`)
      .then((res: any) => {
        if (res.success) setSubmission(res.data)
        else notify('error', res.message)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (canReview) fetchDetail()
    setShowNin(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const goToNextInQueue = () => {
    get('kyc/admin/queue?status=pending_review&page=1&limit=1').then(
      (res: any) => {
        const next = res?.data?.docs?.[0]
        if (next && next._id !== id) {
          navigate(`/admin/kyc-request/${next._id}`)
        } else {
          navigate('/admin/kyc-review')
        }
      },
    )
  }

  const approve = () => {
    setActionLoading(true)
    patch(`kyc/admin/${id}/approve`, {})
      .then((res: any) => {
        if (res.success) {
          notify('success', 'KYC approved — customer has been notified')
          setShowApprove(false)
          goToNextInQueue()
        } else notify('error', res.message)
        setActionLoading(false)
      })
      .catch(() => setActionLoading(false))
  }

  const user = submission?.userId
  const addressAgeDays = submission?.proofOfAddress?.documentDate
    ? Math.floor(
        (Date.now() - new Date(submission.proofOfAddress.documentDate).getTime()) /
          (24 * 60 * 60 * 1000),
      )
    : null

  if (!canReview) {
    return (
      <div className='kyc-detail'>
        <div className='restricted-card'>
          <Icon icon='mdi:shield-lock' />
          <h1>Restricted</h1>
          <p>KYC review is only available to super admins and customer service.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='kyc-detail'>
        <FormLoader position='center' />
      </div>
    )
  }

  if (!submission) {
    return (
      <div className='kyc-detail'>
        <div className='restricted-card'>
          <Icon icon='mdi:file-question-outline' />
          <h1>Not found</h1>
          <p>This KYC submission does not exist.</p>
        </div>
      </div>
    )
  }

  const activeUrl = submission.documents?.[activeDoc]
  const activeMeta = submission[activeDoc]
  const isPdf = activeMeta?.format === 'pdf'
  const isPending = submission.status === 'pending_review'
  const ownSubmission =
    submission.submittedBy &&
    [profile?.id, profile?._id].includes(submission.submittedBy._id)

  return (
    <div className='kyc-detail'>
      {showApprove && (
        <Modal position='center'>
          <div className='kyc-confirm-modal'>
            <div className='modal-header'>
              <div className='modal-icon icon-green'>
                <Icon icon='mdi:shield-check' />
              </div>
              <h2>Approve Verification</h2>
              <p>
                You are confirming that <b>{user?.firstname} {user?.lastname}</b>'s
                identity documents are genuine, match the account details, and the
                proof of address is less than 3 months old.
              </p>
            </div>
            {actionLoading ? (
              <FormLoader position='center' />
            ) : (
              <div className='modal-actions'>
                <button className='btn-cancel' onClick={() => setShowApprove(false)}>
                  Cancel
                </button>
                <button className='btn-approve' onClick={approve}>
                  Approve KYC
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showReject && (
        <RejectKycModal
          submissionId={id!}
          customerName={`${user?.firstname} ${user?.lastname}`}
          onClose={() => setShowReject(false)}
          onSuccess={goToNextInQueue}
        />
      )}

      {lightbox && activeUrl && !isPdf && (
        <div className='kyc-lightbox' onClick={() => setLightbox(false)}>
          <button className='close'>
            <Icon icon='mdi:close' />
          </button>
          <img
            src={activeUrl}
            alt={activeDoc}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className='top flex justify-between items-center flex-wrap'>
        <div
          className='back-btn flex items-center'
          onClick={() => navigate('/admin/kyc-review')}
        >
          <Icon icon='ion:return-up-back-outline' />
          <p>Back to queue</p>
        </div>
        <div className='top-right flex items-center'>
          <KycStatusPill status={submission.status} />
          <IntelligentCheckBadge review={submission.intelligentCheck} />
          {isPending && (
            <span className='waiting-tag'>
              waiting {waitingDays(submission.createdAt)}
            </span>
          )}
        </div>
      </div>

      <div className='detail-grid'>
        {/* ── Documents ─────────────────────────────── */}
        <div className='documents-panel'>
          <div className='doc-tabs'>
            {DOC_TABS.map((tab) => (
              <button
                key={tab.key}
                className={activeDoc === tab.key ? 'active' : ''}
                onClick={() => setActiveDoc(tab.key)}
              >
                <Icon icon={tab.icon} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className='doc-meta flex justify-between items-center flex-wrap'>
            <p className='doc-type'>
              {DOC_TYPE_LABELS[activeMeta?.documentType] ||
                (activeDoc === 'selfieWithId' ? 'Live selfie holding the ID' : '')}
            </p>
            {activeDoc === 'proofOfAddress' && addressAgeDays !== null && (
              <span className={`age-tag ${addressAgeDays > 92 ? 'bad' : 'good'}`}>
                <Icon
                  icon={addressAgeDays > 92 ? 'mdi:alert-circle' : 'mdi:check-circle'}
                />
                Document dated{' '}
                {new Date(submission.proofOfAddress.documentDate).toLocaleDateString()} (
                {addressAgeDays} days old)
              </span>
            )}
          </div>

          <div className='doc-stage'>
            {submission.documentsPurgedAt ? (
              <div className='doc-missing'>
                <Icon icon='mdi:file-remove-outline' />
                <p>
                  Documents were purged on{' '}
                  {new Date(submission.documentsPurgedAt).toLocaleDateString()} under
                  the retention policy.
                </p>
              </div>
            ) : !activeUrl ? (
              <div className='doc-missing'>
                <Icon icon='mdi:file-alert-outline' />
                <p>Document link expired.</p>
                <button onClick={fetchDetail}>Reload documents</button>
              </div>
            ) : isPdf ? (
              <div className='doc-pdf'>
                <Icon icon='mdi:file-pdf-box' />
                <p>This document is a PDF.</p>
                <button onClick={() => window.open(activeUrl, '_blank')}>
                  <Icon icon='mdi:open-in-new' /> Open document
                </button>
              </div>
            ) : (
              <div className='doc-image' onClick={() => setLightbox(true)}>
                <img src={activeUrl} alt={activeDoc} />
                <div className='zoom-hint'>
                  <Icon icon='mdi:magnify-plus-outline' /> Click to zoom
                </div>
              </div>
            )}
          </div>
          <p className='signed-note'>
            <Icon icon='mdi:lock-outline' /> Secure links expire after 10 minutes —
            reload the page to regenerate them.
          </p>

          <IntelligentCheckList review={submission.intelligentCheck} image={activeDoc} />
        </div>

        {/* ── Customer + decision ───────────────────── */}
        <div className='side-panel'>
          <div className='customer-card'>
            <div className='card-header'>
              <div className='avatar'>
                {user?.firstname?.[0]}
                {user?.lastname?.[0]}
              </div>
              <div>
                <h2>
                  {user?.firstname} {user?.lastname}
                </h2>
                <p>@{user?.username}</p>
              </div>
              <button
                className='profile-link'
                title='Open customer profile'
                onClick={() => navigate(`/admin/customer/${user?._id}`)}
              >
                <Icon icon='mdi:open-in-new' />
              </button>
            </div>

            <div className='fields'>
              <div className='field'>
                <p>Email</p>
                <h4>{user?.email}</h4>
              </div>
              <div className='field'>
                <p>Phone</p>
                <h4>{user?.phonenumber}</h4>
              </div>
              <div className='field'>
                <p>Customer since</p>
                <h4>
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : '—'}
                </h4>
              </div>
              <div className='field'>
                <p>Address on file</p>
                <h4>
                  {[user?.address, user?.city, user?.state]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </h4>
              </div>
              <div className='field nin-field'>
                <p>NIN</p>
                <div className='flex items-center'>
                  <h4 className='mono'>{showNin ? submission.nin : submission.ninMasked}</h4>
                  <button onClick={() => setShowNin(!showNin)} title={showNin ? 'Hide' : 'Reveal'}>
                    <Icon icon={showNin ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className='submission-card'>
            <h3>Submission</h3>
            <div className='fields'>
              <div className='field'>
                <p>Submitted</p>
                <h4>{new Date(submission.createdAt).toLocaleString()}</h4>
              </div>
              <div className='field'>
                <p>Attempt</p>
                <h4>#{submission.version}</h4>
              </div>
              <div className='field'>
                <p>Channel</p>
                <h4>
                  {submission.submittedVia === 'agent' ? (
                    <span className='via-badge agent'>
                      <Icon icon='mdi:account-tie' /> Agent-assisted
                    </span>
                  ) : (
                    <span className='via-badge user'>
                      <Icon icon='mdi:account' /> Customer
                    </span>
                  )}
                </h4>
              </div>
              {submission.submittedBy && (
                <div className='field'>
                  <p>Filled by</p>
                  <h4>
                    {submission.submittedBy.name}{' '}
                    <span className='muted'>({submission.submittedBy.role})</span>
                  </h4>
                </div>
              )}
              {submission.reviewedBy && (
                <div className='field'>
                  <p>Reviewed by</p>
                  <h4>
                    {submission.reviewedBy.name} ·{' '}
                    {submission.reviewedAt
                      ? new Date(submission.reviewedAt).toLocaleString()
                      : ''}
                  </h4>
                </div>
              )}
              {submission.rejectionReason && (
                <div className='field'>
                  <p>Rejection reason</p>
                  <h4 className='reason'>{submission.rejectionReason}</h4>
                </div>
              )}
              <div className='field'>
                <p>Declaration</p>
                <h4>
                  {submission.declarationAccepted ? (
                    <span className='decl ok'>
                      <Icon icon='mdi:check-circle' /> Accepted{' '}
                      {new Date(submission.declarationAcceptedAt).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className='decl bad'>Not accepted</span>
                  )}
                </h4>
              </div>
            </div>
          </div>

          <IntelligentCheckSummary
            review={submission.intelligentCheck}
            images={DOC_TABS}
            onSelect={(key) => setActiveDoc(key as DocKey)}
          />

          {isPending && (
            <div className='decision-card'>
              <h3>Decision</h3>
              <div className='checklist'>
                <p>
                  <Icon icon='mdi:check' /> Photo ID is clear and not expired
                </p>
                <p>
                  <Icon icon='mdi:check' /> Selfie matches the photo on the ID
                </p>
                <p>
                  <Icon icon='mdi:check' /> Name matches the account details
                </p>
                <p>
                  <Icon icon='mdi:check' /> Proof of address is under 3 months old
                </p>
              </div>
              {ownSubmission ? (
                <div className='own-submission'>
                  <Icon icon='mdi:account-alert-outline' />
                  <p>
                    You filled this submission, so another reviewer must approve or
                    reject it.
                  </p>
                </div>
              ) : (
                <div className='actions'>
                  <button className='btn-reject' onClick={() => setShowReject(true)}>
                    <Icon icon='mdi:close-circle-outline' /> Reject
                  </button>
                  <button className='btn-approve' onClick={() => setShowApprove(true)}>
                    <Icon icon='mdi:shield-check-outline' /> Approve
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default KycDetail
